precision highp float;

varying vec2 vUv;
uniform float uTime;
uniform float uHover;
uniform vec2  uResolution;
uniform float uAudioLevel;
uniform float uBass;
uniform float uMid;
uniform float uTreble;
uniform vec3  uObjectColor;
uniform vec3  uBgColor;
uniform float uSphereSize;
uniform float uLightAngle;

#define PI 3.14159265359

const vec3 COLOR_PLUM = vec3(0.439, 0.106, 0.082);
const vec3 COLOR_GOLD = vec3(1.0, 0.84, 0.0);

mat2 rotate(float a) {
    float s = sin(a), c = cos(a);
    return mat2(c, -s, s, c);
}

float hash(float n)   { return fract(sin(n) * 43758.5453123); }
float hash2d(vec2 p)  { return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453); }

vec3 heatToColor(float h, vec3 hotColor) {
    vec3 caramel = min(hotColor + vec3(0.25, 0.15, 0.05), vec3(1.0));
    if (h > 0.88) return mix(hotColor, caramel, (h - 0.88) * 8.33);
    if (h > 0.45) return mix(COLOR_PLUM * 1.8, hotColor, (h - 0.45) * 2.33);
    return COLOR_PLUM * smoothstep(0.0, 0.45, h);
}

void main() {
    vec2 uv = vUv - 0.5;
    float asp = uResolution.x / uResolution.y;
    uv.x *= asp;

    float dist   = length(uv);
    float radius = (0.27 + uBass * 0.02) * uSphereSize;

    vec3 hotColor = uObjectColor;
    vec3 caramel  = min(uObjectColor + vec3(0.25, 0.15, 0.05), vec3(1.0));

    // --- 1. STARS ---
    vec2 starUV = uv * 15.0;
    vec2 sid    = floor(starUV);
    vec2 sfv    = fract(starUV) - 0.5;
    float sh    = hash2d(sid);
    float tw    = sin(uTime * 3.0 + sh * 6.28) * 0.5 + 0.5;
    vec3 starsColor = smoothstep(0.03 * sh, 0.0, length(sfv)) * tw * COLOR_GOLD * 0.8;

    // --- 2. EMBERS ---
    vec3 emberColor = vec3(0.0);
    float bassOn = smoothstep(0.08, 0.35, uBass);

    for (float i = 0.0; i < 20.0; i += 1.0) {
        float cycleDur   = 1.2 + hash(i * 7.31) * 2.0;
        float cyclePhase = fract(uTime / cycleDur + hash(i * 0.41));
        float seed       = hash(floor(uTime / cycleDur + hash(i * 0.41)) * 1.71 + i * 83.3);
        float heat       = 1.0 - cyclePhase;
        float alpha      = smoothstep(0.0, 0.12, heat) * heat * bassOn;
        vec2  spawnDir   = vec2(cos(seed * 6.2832), sin(seed * 6.2832));
        float speed      = (0.055 + hash(seed * 33.1) * 0.12) * (0.8 + uBass * 0.6);
        vec2  drift      = vec2(hash(seed * 77.1) - 0.5, hash(seed * 55.3) - 0.5) * 0.07;
        vec2  pos        = spawnDir * (radius + cyclePhase * speed) + drift * cyclePhase * cyclePhase;
        float sz         = (0.004 + hash(seed * 21.7) * 0.007) * (1.0 + smoothstep(0.6, 0.0, heat) * 2.5);
        float d          = length(uv - pos);
        emberColor += heatToColor(heat, hotColor) * (
            exp(-d * d / max(sz * sz,       1e-7)) * alpha +
            exp(-d * d / max(sz * sz * 9.0, 1e-6)) * alpha * 0.45
        );
    }
    for (float i = 0.0; i < 10.0; i += 1.0) {
        float spDur   = 0.35 + hash(i * 13.7) * 0.55;
        float spPhase = fract(uTime / spDur + hash(i * 2.3 + 100.0));
        float spSeed  = hash(floor(uTime / spDur + hash(i * 2.3 + 100.0)) * 3.1 + i * 61.7);
        float spHeat  = 1.0 - spPhase;
        vec2  spPos   = vec2(cos(spSeed * 6.2832), sin(spSeed * 6.2832)) * (radius + spPhase * (0.12 + hash(spSeed * 41.3) * 0.22) * (0.9 + uBass * 0.4));
        float spSz    = 0.0018 + hash(spSeed * 17.9) * 0.0022;
        float spD     = length(uv - spPos);
        emberColor   += mix(hotColor, caramel, spHeat * spHeat) * exp(-spD * spD / max(spSz * spSz, 1e-8)) * spHeat * spHeat * bassOn * 2.5;
    }

    // --- 3. SPHERE ---
    vec3  finalColor = starsColor + uBgColor + emberColor;
    float animSpeed  = uTime * (1.0 + uAudioLevel * 2.0);

    if (dist < radius + 0.5) {
        float z = sqrt(max(0.0, radius * radius - dist * dist));

        // Static normal for lighting (world space, not rotated with animation)
        vec3 staticNrm = normalize(vec3(uv, z));

        // Directional light from uLightAngle
        float lightRad = uLightAngle * (PI / 180.0);
        vec3  lightDir = normalize(vec3(cos(lightRad) * 0.7, sin(lightRad) * 0.7, 1.0));
        float diffuse  = max(0.0, dot(staticNrm, lightDir));
        float spec     = pow(max(0.0, dot(reflect(-lightDir, staticNrm), vec3(0.0, 0.0, 1.0))), 40.0);

        // Animated normal for grid/lightning
        vec3 nrm = normalize(vec3(uv, z));
        nrm.xz *= rotate(uTime * 0.15);
        nrm.xy *= rotate(uTime * 0.05);

        float phi   = atan(nrm.z, nrm.x);
        float theta = acos(nrm.y);
        float edge  = smoothstep(radius, radius - 0.08, dist);
        float gd    = 12.0;
        float grid  = clamp(
            smoothstep(0.025, 0.0, abs(sin(phi   * gd))) +
            smoothstep(0.025, 0.0, abs(sin(theta * gd))),
            0.0, 1.0) * edge;
        float lightning = grid * smoothstep(0.98, 1.0, cos(phi   * gd * 0.5 + animSpeed))
                        + grid * smoothstep(0.98, 1.0, sin(theta * gd * 0.5 - animSpeed * 1.3));
        float body  = (dist < radius) ? (1.0 - dist / radius) * 0.03 : 0.0;
        float glow  = exp(-(dist - radius) * 12.0) * (uAudioLevel * 0.1 + 0.1);
        float mask  = smoothstep(radius + 0.4, radius - 0.05, dist);

        // Light shading — diffuse warmth + specular highlight
        vec3 lightLayer = hotColor  * diffuse * 0.45
                        + vec3(1.0) * spec    * 0.70;

        vec3 layer = mix(COLOR_PLUM, caramel, grid) * grid
                   + COLOR_PLUM * body * 0.5
                   + mix(hotColor, caramel, lightning) * lightning * 1.5
                   + hotColor  * glow  * 0.3
                   + lightLayer * edge * 0.55 * (1.0 + uHover * 0.2);

        finalColor = mix(finalColor, finalColor + layer, mask);
    }

    float alpha = clamp(length(finalColor) * 2.0, 0.0, 1.0);
    gl_FragColor = vec4(finalColor, alpha);
}
